import { toLocalISODate } from '../utils/dateUtils';
import { confirmDialog, notify } from '../utils/notify';
import React, { useState, useEffect } from 'react';
import { payrollAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Calculator, CalendarDays, Check, ChevronDown, ChevronRight, Info, Landmark, ListChecks, Plus, Receipt, Save, Search,
  TriangleAlert, Users, Wallet, X,
} from 'lucide-react';
import Modal from './Modal';
import StatusBadge from './StatusBadge';
import './PayrollCalculator.css';

const PayrollCalculator = ({ employees, onClose, onSave }) => {
  const { t, locale } = useLanguage();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    selected_employees: [],
    period_start: '',
    period_end: '',
    payment_date: '',
    currency: 'SRD'
  });

  // The adjustment being typed, per employee: { kind, description, amount, reason, error }
  const [adjustmentForms, setAdjustmentForms] = useState({});
  const [previews, setPreviews] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [departmentFilter, setDepartmentFilter] = useState('all');

  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setFormData(prev => ({
      ...prev,
      period_start: toLocalISODate(firstDay),
      period_end: toLocalISODate(lastDay),
      payment_date: toLocalISODate(lastDay)
    }));

  }, []);

  // Get unique departments
  const departments = [...new Set(employees.map(e => e.department))].filter(Boolean);

  // Filter and paginate employees
  const getFilteredEmployees = () => {
    let filtered = employees;

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(search) ||
        emp.position?.toLowerCase().includes(search) ||
        emp.department?.toLowerCase().includes(search) ||
        emp.id.toString().includes(search)
      );
    }

    // Filter by department
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(emp => emp.department === departmentFilter);
    }

    return filtered;
  };

  const filteredEmployees = getFilteredEmployees();

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentEmployees = filteredEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const handleEmployeeToggle = (empId) => {
    setFormData(prev => ({
      ...prev,
      selected_employees: prev.selected_employees.includes(empId)
        ? prev.selected_employees.filter(id => id !== empId)
        : [...prev.selected_employees, empId]
    }));
  };

  const handleSelectAll = () => {
    setFormData(prev => ({
      ...prev,
      selected_employees: prev.selected_employees.length === employees.length
        ? []
        : employees.map(e => e.id)
    }));
  };

  const handleSelectPage = () => {
    const currentIds = currentEmployees.map(e => e.id);
    const allCurrentSelected = currentIds.every(id => formData.selected_employees.includes(id));
    
    if (allCurrentSelected) {
      // Deselect current page
      setFormData(prev => ({
        ...prev,
        selected_employees: prev.selected_employees.filter(id => !currentIds.includes(id))
      }));
    } else {
      // Select current page
      setFormData(prev => ({
        ...prev,
        selected_employees: [...new Set([...prev.selected_employees, ...currentIds])]
      }));
    }
  };

  const handleCalculatePreview = async () => {
    if (formData.selected_employees.length === 0) {
      notify(t('Please select at least one employee'), 'warning');
      return;
    }
  
    try {
      setLoading(true);
      const results = [];
      const errors = [];
  
      for (const empId of formData.selected_employees) {
        try {
          const employee = employees.find(e => e.id === empId);
  
          if (!employee) {
            errors.push(t('Employee ID {id} not found', { id: empId }));
            continue;
          }
  
          const previewData = {
            employee_id: empId,
            period_start: formData.period_start,
            period_end: formData.period_end
          };
  
          const response = await payrollAPI.preview(previewData);
  
          // ✅ MEJORA: Manejo más robusto de respuestas
          // Casos donde debemos saltar al empleado:
          // 1. success = false
          // 2. skipped = true
          // 3. preview = null
          // 4. No hay datos de asistencia
          if (
            !response.data.success || 
            response.data.skipped || 
            response.data.preview === null ||
            !response.data.preview
          ) {
            const message = response.data.message || t('No attendance records found');
            console.log(`⏭️ Skipping ${employee.first_name} ${employee.last_name}: ${message}`);
            errors.push(`${employee.first_name} ${employee.last_name}: ${message}`);
            continue;
          }
  
          // ✅ Validación adicional: verificar que haya datos mínimos
          const preview = response.data.preview;
          if (!preview.gross_pay || preview.gross_pay === 0) {
            console.log(`⏭️ Skipping ${employee.first_name} ${employee.last_name}: Zero gross pay`);
            errors.push(`${employee.first_name} ${employee.last_name}: ${t('No salary calculated (possibly no work days)')}`);
            continue;
          }
          
          results.push({
            employee_id: empId,
            employee_name: `${employee.first_name} ${employee.last_name}`,
            ...preview
          });
        } catch (error) {
          console.error(`Error calculating for employee ${empId}:`, error);
          const employee = employees.find(e => e.id === empId);
          const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : `ID ${empId}`;
          
          // ✅ MEJORA: Extraer mensaje específico del error
          let errorMessage = error.message;
          if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response?.status === 400) {
            errorMessage = t('Invalid data or no attendance records');
          }
          
          errors.push(`${employeeName}: ${errorMessage}`);
        }
      }
  
      // ✅ MEJORA: Mostrar errores de forma más clara
      if (errors.length > 0) {
        const errorList = errors.map((err, idx) => `${idx + 1}. ${err}`).join('\n');
        notify(
          `${t('Some employees were skipped:')}\n\n${errorList}\n\n` +
          `${results.length > 0 ? t('Successfully calculated {count} payroll(s)', { count: results.length }) : t('No payrolls could be calculated')}`
        , 'warning');
      }
  
      if (results.length === 0) {
        notify(
          t('No payroll previews could be generated.') + '\n\n' +
          t('Possible reasons:') + '\n' +
          t('• No attendance records for the selected period') + '\n' +
          t('• No worked days/hours recorded') + '\n' +
          t('• Invalid employee data') + '\n\n' +
          t('Please check the attendance records and try again.')
        , 'error');
        setLoading(false);
        return;
      }
  
      setPreviews(results);
  
      setAdjustmentForms({});
  
      // Expand all cards by default
      const expanded = {};
      results.forEach(p => {
        expanded[p.employee_id] = true;
      });
      setExpandedCards(expanded);
  
      setStep(2);
    } catch (error) {
      console.error('Error calculating preview:', error);
      const errorMsg = error.response?.data?.message || error.message || t('Unknown error calculating payroll');
      notify(`${t('Error calculating payroll:')}\n\n${errorMsg}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Taxes and deductions shown here are the server's preview, and the server
  // calculates the payroll again when it is saved. What a person can add are
  // adjustment lines, after tax, each with a reason (saved with who and when).
  const ADJUSTMENT_AMOUNT = /^\d{1,9}(\.\d{1,2})?$/;

  const withDeductions = (preview, deductions) => {
    const otherDeductions = deductions.reduce((sum, d) => sum + (d.amount || 0), 0);
    return { ...preview, deductions, other_deductions: otherDeductions, net_pay: preview.gross_pay - preview.tax_deduction - otherDeductions };
  };

  const adjustmentFormOf = (employeeId) => ({
    kind: 'deduction', description: '', amount: '', reason: '', error: null, ...adjustmentForms[employeeId]
  });

  const setAdjustmentField = (employeeId, field, value) => {
    setAdjustmentForms(prev => ({
      ...prev,
      [employeeId]: { ...adjustmentFormOf(employeeId), ...prev[employeeId], [field]: value, error: null }
    }));
  };

  const addAdjustment = (employeeId) => {
    const form = adjustmentFormOf(employeeId);
    const description = form.description.trim();
    const reason = form.reason.trim();
    const amountText = String(form.amount).trim();
    let error = null;
    if (!description) error = t('Write what the adjustment is');
    else if (!ADJUSTMENT_AMOUNT.test(amountText) || !(Number(amountText) > 0)) error = t('The amount must be a positive number with at most 2 decimals');
    else if (!reason) error = t('Write the reason for the adjustment');
    if (error) {
      setAdjustmentForms(prev => ({ ...prev, [employeeId]: { ...form, error } }));
      return;
    }
    const amount = Number(amountText);
    setPreviews(prev => prev.map(p => (p.employee_id !== employeeId ? p : withDeductions(p, [
      ...(p.deductions || []),
      {
        code: `ADJ_${Date.now()}`,
        name: description,
        type: 'adjustment',
        kind: form.kind,
        amount: form.kind === 'deduction' ? amount : -amount,
        reason
      }
    ]))));
    setAdjustmentForms(prev => ({ ...prev, [employeeId]: { kind: form.kind, description: '', amount: '', reason: '', error: null } }));
  };

  const removeAdjustment = (employeeId, code) => {
    setPreviews(prev => prev.map(p => (p.employee_id !== employeeId ? p
      : withDeductions(p, (p.deductions || []).filter(d => d.code !== code)))));
  };

  // What is sent when saving: only the adjustment lines.
  const adjustmentsOf = (preview) => (preview.deductions || [])
    .filter(d => d.type === 'adjustment')
    .map(d => ({ kind: d.kind, description: d.name, amount: Math.abs(d.amount).toFixed(2), reason: d.reason }));

  const toggleCardExpansion = (employeeId) => {
    setExpandedCards(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  const handleSaveAll = async () => {
    if (!await confirmDialog(t('Are you sure you want to create {count} payroll record(s)?', { count: previews.length }))) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      const errors = [];

      for (const preview of previews) {
        try {
          const response = await payrollAPI.calculate({
            employee_id: preview.employee_id,
            period_start: formData.period_start,
            period_end: formData.period_end,
            payment_date: formData.payment_date || undefined,
            adjustments: adjustmentsOf(preview)
          });
          // The server calculates it again: a payroll that already exists, or
          // adjustments that would leave a negative net, come back in errors.
          if (!response.data.payroll || response.data.payroll.length === 0) {
            throw new Error(response.data.errors?.[0]?.error || t('Payroll was not saved'));
          }
          successCount++;
        } catch (error) {
          console.error(`Error saving payroll for employee ${preview.employee_id}:`, error);
          errors.push(`${preview.employee_name}: ${error.response?.data?.message || error.message}`);
        }
      }

      if (successCount > 0) {
        notify(`${t('Successfully created {count} payroll record(s)!', { count: successCount })}${errors.length > 0 ? `\n\n${t('Errors:')}\n${errors.join('\n')}` : ''}`, errors.length > 0 ? 'warning' : 'success');
        onSave();
        onClose();
      } else {
        notify(`${t('Failed to create payroll records:')}\n\n${errors.join('\n')}`, 'error');
      }
    } catch (error) {
      console.error('Error saving payroll:', error);
      notify(t('Error saving payroll records'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getTotalSummary = () => {
    return previews.reduce((acc, p) => ({
      totalGross: acc.totalGross + (p.gross_pay || 0),
      totalTax: acc.totalTax + (p.tax_deduction || 0),
      totalDeductions: acc.totalDeductions + (p.other_deductions || 0),
      totalNet: acc.totalNet + (p.net_pay || 0)
    }), { totalGross: 0, totalTax: 0, totalDeductions: 0, totalNet: 0 });
  };

  return (
    <Modal
      open
      title={t('Payroll Calculator')}
      subtitle={t('Calculate and review employee payroll')}
      onClose={onClose}
      size="xl"
    >
      <div className="pc-dialog">

        {/* STEP 1: Selection */}
        {step === 1 && (
          <div className="pc-body">
            <div className="pc-steps">
              <div className="pc-step is-active">
                <span className="pc-step__number">1</span>
                <span className="pc-step__title">{t('Select & Configure')}</span>
              </div>
              <div className="pc-step">
                <span className="pc-step__number">2</span>
                <span className="pc-step__title">{t('Review & Adjust')}</span>
              </div>
            </div>

            {/* Employee Selection */}
            <div className="pc-section">
              <div className="pc-section__head">
                <h3 className="pc-section__title"><Users aria-hidden="true" /> {t('Select Employees')}</h3>
                <div className="pc-section__actions">
                  <button onClick={handleSelectPage} className="btn btn--secondary">
                    {currentEmployees.every(e => formData.selected_employees.includes(e.id)) 
                      ? t('Deselect Page') 
                      : t('Select Page')}
                  </button>
                  <button onClick={handleSelectAll} className="btn btn--secondary">
                    {formData.selected_employees.length === employees.length ? t('Deselect All') : t('Select All')}
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="pc-filters">
                <div className="pc-search">
                  <Search aria-hidden="true" />
                  <input className="input"
                    type="text"
                    placeholder={t('Search by name, position, department, or ID...')}
                    aria-label={t('Search by name, position, department, or ID...')}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                <div className="pc-department">
                  <select className="select"
                    value={departmentFilter}
                    onChange={(e) => {
                      setDepartmentFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="all">{t('All Departments')} ({employees.length})</option>
                    {departments.map(dept => (
                      <option key={dept} value={dept}>
                        {dept} ({employees.filter(e => e.department === dept).length})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Results info */}
              <div className="pc-results">
                {t('Showing {from}-{to} of {total} employees', { from: indexOfFirstItem + 1, to: Math.min(indexOfLastItem, filteredEmployees.length), total: filteredEmployees.length })}
                {searchTerm && ` ${t('(filtered from {count} total)', { count: employees.length })}`}
              </div>

              <div className="pc-employees">
                {currentEmployees.map(emp => (
                  <label key={emp.id} className={`pc-employee${formData.selected_employees.includes(emp.id) ? ' is-selected' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.selected_employees.includes(emp.id)}
                      onChange={() => handleEmployeeToggle(emp.id)}
                    />
                    <div className="pc-employee__content">
                      <div className="pc-employee__name">{emp.first_name} {emp.last_name}</div>
                      <div className="pc-meta">
                        <span>{emp.position}</span>
                        <span className="pc-meta__dot">•</span>
                        <span>{emp.department}</span>
                      </div>
                      <div className="pc-employee__salary">
                        {emp.currency} {parseFloat(emp.salary || 0).toLocaleString(locale)}
                      </div>
                    </div>
                    <div className="pc-employee__check"><Check aria-hidden="true" /></div>
                  </label>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pc-pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="btn btn--secondary"
                  >
                    {t('← Previous')}
                  </button>
                  
                  <span className="pc-page-info">
                    {t('Page {current} of {total}', { current: currentPage, total: totalPages })}
                  </span>
                  
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="btn btn--secondary"
                  >
                    {t('Next →')}
                  </button>
                </div>
              )}

              <div className="pc-selection">
                <strong>{formData.selected_employees.length}</strong> {t('employee(s) selected')}
              </div>
            </div>

            {/* Period Configuration */}
            <div className="pc-section">
              <h3 className="pc-section__title"><CalendarDays aria-hidden="true" /> {t('Pay Period')}</h3>
              <div className="pc-form-row">
                <div className="pc-field">
                  <label className="field-label">{t('Period Start')}</label>
                  <input className="input"
                    type="date"
                    value={formData.period_start}
                    onChange={(e) => setFormData({...formData, period_start: e.target.value})}
                    required
                  />
                </div>
                <div className="pc-field">
                  <label className="field-label">{t('Period End')}</label>
                  <input className="input"
                    type="date"
                    value={formData.period_end}
                    onChange={(e) => setFormData({...formData, period_end: e.target.value})}
                    required
                  />
                </div>
                <div className="pc-field">
                  <label className="field-label">{t('Payment Date')}</label>
                  <input className="input"
                    type="date"
                    value={formData.payment_date}
                    onChange={(e) => setFormData({...formData, payment_date: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="pc-form-actions">
              <button onClick={onClose} className="btn btn--secondary">{t('Cancel')}</button>
              <button
                onClick={handleCalculatePreview}
                className="btn btn--primary"
                disabled={loading || formData.selected_employees.length === 0}
              >
                {loading ? (
                  <>{t('Calculating...')}</>
                ) : (
                  <><Calculator aria-hidden="true" /> {t('Calculate for {count} Employee(s)', { count: formData.selected_employees.length })}</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: Review */}
        {step === 2 && previews.length > 0 && (
          <div className="pc-body">
            <div className="pc-steps">
              <div className="pc-step is-done">
                <span className="pc-step__number"><Check aria-hidden="true" /></span>
                <span className="pc-step__title">{t('Select & Configure')}</span>
              </div>
              <div className="pc-step is-active">
                <span className="pc-step__number">2</span>
                <span className="pc-step__title">{t('Review & Adjust')}</span>
              </div>
            </div>

            {/* Overall Summary */}
            <div className="pc-totals">
              <div className="pc-total">
                <span className="pc-total__label">{t('Employees')}</span>
                <span className="pc-total__value">{previews.length}</span>
              </div>
              <div className="pc-total">
                <span className="pc-total__label">{t('Total Gross')}</span>
                <span className="pc-total__value">
                  {previews[0]?.currency} {getTotalSummary().totalGross.toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="pc-total">
                <span className="pc-total__label">{t('Total Taxes')}</span>
                <span className="pc-total__value is-minus">
                  -{previews[0]?.currency} {getTotalSummary().totalTax.toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="pc-total">
                <span className="pc-total__label">{t('Total Deductions')}</span>
                <span className="pc-total__value is-minus">
                  -{previews[0]?.currency} {getTotalSummary().totalDeductions.toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
              <div className="pc-total is-net">
                <span className="pc-total__label">{t('Total Net Pay')}</span>
                <span className="pc-total__value">
                  {previews[0]?.currency} {getTotalSummary().totalNet.toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </span>
              </div>
            </div>

            {/* Individual Employee Cards */}
            <div className="pc-cards">
              {previews.map((preview, index) => {
                const isExpanded = expandedCards[preview.employee_id];
                const attendance = preview.attendance || {};
                
                return (
                  <div key={preview.employee_id} className="pc-card">
                    {/* Card Header */}
                    <div className="pc-card__head" onClick={() => toggleCardExpansion(preview.employee_id)}>
                      <div className="pc-card__who">
                        <h3 className="pc-card__name">
                          <span className="pc-card__number">{index + 1}.</span>
                          {preview.employee_name}
                        </h3>
                        <div className="pc-meta">
                          <span>{preview.employee?.position}</span>
                          <span className="pc-meta__dot">•</span>
                          <span>{preview.employee?.department}</span>
                        </div>
                        {(attendance.absent_days > 0 || attendance.sick_days > 0 || attendance.late_minutes > 0 || attendance.overtime_hours > 0) && (
                          <div className="pc-badges">
                            {attendance.absent_days > 0 && (
                              <StatusBadge variant="danger">{attendance.absent_days} {t('Absent')}</StatusBadge>
                            )}
                            {attendance.sick_days > 0 && (
                              <StatusBadge variant="warning">{attendance.sick_days} {t('Sick')}</StatusBadge>
                            )}
                            {attendance.late_minutes > 0 && (
                              <StatusBadge variant="warning">{attendance.late_minutes} {t('min Late')}</StatusBadge>
                            )}
                            {attendance.overtime_hours > 0 && (
                              <StatusBadge variant="success">{attendance.overtime_hours}h {t('OT')}</StatusBadge>
                            )}
                          </div>
                        )}
                        {attendance.expected_work_days && attendance.days_worked < attendance.expected_work_days && (
                          <div className="pc-alert">
                            <Info aria-hidden="true" /> {t('Worked {worked} of {expected} expected days ({pct}%)', { worked: attendance.days_worked, expected: attendance.expected_work_days, pct: (attendance.work_ratio * 100).toFixed(0) })}
                          </div>
                        )}
                        {/* The engine's warnings: negative net pay, a deduction it could not take */}
                        {(preview.warnings || []).map(w => (
                          <div key={`${w.code}-${w.message}`} className="pc-alert pc-warning">
                            <TriangleAlert aria-hidden="true" /> {w.message}
                          </div>
                        ))}
                      </div>
                      <div className="pc-card__amounts">
                        <div className="pc-amount">
                          <span className="pc-amount__label">{t('Gross')}</span>
                          <span className="pc-amount__value">{preview.currency} {(preview.gross_pay || 0).toLocaleString(locale, {minimumFractionDigits: 2})}</span>
                        </div>
                        <div className="pc-amount is-net">
                          <span className="pc-amount__label">{t('Net Pay')}</span>
                          <span className="pc-amount__value">{preview.currency} {(preview.net_pay || 0).toLocaleString(locale, {minimumFractionDigits: 2})}</span>
                        </div>
                        <button type="button" className="btn btn--ghost btn--icon" aria-expanded={Boolean(isExpanded)} aria-label={preview.employee_name}>
                          {isExpanded ? <ChevronDown aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
                        </button>
                      </div>
                    </div>

                    {/* Card Body - Expandable */}
                    {isExpanded && (
                      <div className="pc-card__body">
                        {/* Attendance Summary */}
                        {attendance && (
                          <div className="pc-block">
                            <h4 className="pc-block__title"><ListChecks aria-hidden="true" /> {t('Attendance & Work Summary')}</h4>
                            <div className="pc-info-grid">
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Days Worked')}</span>
                                <span className="pc-info__value">{attendance.days_worked || 0} / {attendance.expected_work_days || 0}</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Work Ratio')}</span>
                                <span className="pc-info__value">{((attendance.work_ratio || 0) * 100).toFixed(0)}%</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Total Hours')}</span>
                                <span className="pc-info__value">{(attendance.total_hours_worked || 0).toFixed(1)}h</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Overtime')}</span>
                                <span className="pc-info__value">{(attendance.overtime_hours || 0).toFixed(1)}h</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Absent')}</span>
                                <span className="pc-info__value">{attendance.absent_days || 0} {t('days')}</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Sick')}</span>
                                <span className="pc-info__value">{attendance.sick_days || 0} {t('days')}</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Late')}</span>
                                <span className="pc-info__value">{attendance.late_minutes || 0} {t('min')}</span>
                              </div>
                              <div className="pc-info">
                                <span className="pc-info__label">{t('Base Salary')}</span>
                                <span className="pc-info__value">{preview.currency} {(preview.rates?.base_salary || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Earnings Breakdown */}
                        
                        
                        {preview.earnings && preview.earnings.length > 0 && (
                          <div className="pc-block">
                            <h4 className="pc-block__title"><Wallet aria-hidden="true" /> {t('Earnings Breakdown')}</h4>
                            <table className="data-table pc-table">
                              <thead>
                                <tr>
                                  <th>{t('Component')}</th>
                                  <th>{t('Details')}</th>
                                  <th className="num">{t('Amount')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview.earnings.map((earning, idx) => (
                                  <tr key={idx}>
                                    <td className="pc-earning">
                                      <strong>{earning.name || earning.code}</strong>
                                      {earning.type === 'premium' && <StatusBadge variant="neutral">{t('Premium')}</StatusBadge>}
                                      {earning.type === 'overtime' && <StatusBadge variant="success">{t('Overtime')}</StatusBadge>}
                                    </td>
                                    <td className="pc-muted">
                                      {earning.details || earning.calculation || '-'}
                                      {earning.hours && (
                                        <div className="pc-hours">
                                          {earning.hours.toFixed(1)} {t('hrs')} × {preview.currency} {earning.rate.toFixed(2)}/{t('hr')}
                                        </div>
                                      )}
                                    </td>
                                    <td className="num">
                                      {preview.currency} {earning.amount.toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        

                        {/* Taxes: calculated by the server */}
                        <div className="pc-block">
                          <h4 className="pc-block__title"><Landmark aria-hidden="true" /> {t('Taxes')}</h4>
                          <table className="data-table pc-table">
                            <thead>
                              <tr>
                                <th>{t('Tax Type')}</th>
                                <th className="pc-center">{t('Rate (%)')}</th>
                                <th className="num">{t('Amount')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.tax_breakdown && preview.tax_breakdown.map((tax, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <strong>{tax.name}</strong>
                                    {tax.details && <div className="pc-muted">{tax.details}</div>}
                                  </td>
                                  <td className="pc-center">
                                    {tax.rate !== null && tax.rate !== undefined ? parseFloat(tax.rate).toFixed(2) : '0.00'}
                                    {tax.type === 'tiered' && <div className="pc-muted">{t('(effective)')}</div>}
                                  </td>
                                  <td className="num">
                                    {preview.currency} {(parseFloat(tax.amount) || 0).toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="pc-total-row">
                                <td colSpan="2"><strong>{t('Total Taxes')}</strong></td>
                                <td className="num"><strong>{preview.currency} {preview.tax_deduction.toLocaleString(locale, {minimumFractionDigits: 2})}</strong></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {/* Deductions: the server's, plus adjustment lines */}
                        <div className="pc-block">
                          <h4 className="pc-block__title"><Receipt aria-hidden="true" /> {t('Deductions and adjustments')}</h4>
                          <table className="data-table pc-table">
                            <thead>
                              <tr>
                                <th>{t('Deduction')}</th>
                                <th>{t('Type')}</th>
                                <th>{t('Details')}</th>
                                <th className="num">{t('Amount')}</th>
                                <th className="pc-center">{t('Actions')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {preview.deductions && preview.deductions.map((ded, idx) => (
                                <tr key={ded.code || idx} className={ded.type === 'absence' || ded.type === 'sick' || ded.type === 'late' ? 'highlight-row' : ''}>
                                  <td><strong>{ded.name}</strong></td>
                                  <td>
                                    {ded.type === 'adjustment' ? (
                                      <StatusBadge variant="neutral">{ded.kind === 'addition' ? t('Adjustment: added') : t('Adjustment: deducted')}</StatusBadge>
                                    ) : (
                                      <StatusBadge variant={ded.type === 'absence' ? 'danger' : ded.type === 'sick' || ded.type === 'late' ? 'warning' : 'neutral'}>
                                        {t(ded.type)}
                                      </StatusBadge>
                                    )}
                                  </td>
                                  <td className="pc-muted">{ded.type === 'adjustment' ? ded.reason : (ded.details || ded.calculation || '-')}</td>
                                  <td className="num">
                                    {preview.currency} {(ded.amount || 0).toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                  </td>
                                  <td className="pc-center">
                                    {ded.type === 'adjustment' && (
                                      <button
                                        type="button"
                                        onClick={() => removeAdjustment(preview.employee_id, ded.code)}
                                        className="btn btn--secondary btn--icon btn--danger-text"
                                        title={t('Remove adjustment')}
                                        aria-label={t('Remove adjustment')}
                                      >
                                        <X aria-hidden="true" />
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="pc-total-row">
                                <td colSpan="3"><strong>{t('Total Deductions')}</strong></td>
                                <td className="num"><strong>{preview.currency} {preview.other_deductions.toLocaleString(locale, {minimumFractionDigits: 2})}</strong></td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                          <div className="pc-adjust">
                            <select className="select"
                              aria-label={t('Adjustment type')}
                              value={adjustmentFormOf(preview.employee_id).kind}
                              onChange={(e) => setAdjustmentField(preview.employee_id, 'kind', e.target.value)}
                            >
                              <option value="deduction">{t('Deduct from net pay')}</option>
                              <option value="addition">{t('Add to net pay')}</option>
                            </select>
                            <input className="input"
                              type="text"
                              maxLength={120}
                              aria-label={t('Adjustment description')}
                              placeholder={t('What (e.g. uniform not returned)')}
                              value={adjustmentFormOf(preview.employee_id).description}
                              onChange={(e) => setAdjustmentField(preview.employee_id, 'description', e.target.value)}
                            />
                            <input className="input"
                              type="number"
                              min="0.01"
                              step="0.01"
                              aria-label={t('Adjustment amount')}
                              placeholder={t('Amount')}
                              value={adjustmentFormOf(preview.employee_id).amount}
                              onChange={(e) => setAdjustmentField(preview.employee_id, 'amount', e.target.value)}
                            />
                            <input className="input"
                              type="text"
                              maxLength={500}
                              aria-label={t('Adjustment reason')}
                              placeholder={t('Reason (saved with your name)')}
                              value={adjustmentFormOf(preview.employee_id).reason}
                              onChange={(e) => setAdjustmentField(preview.employee_id, 'reason', e.target.value)}
                            />
                            <button type="button" className="btn btn--secondary" onClick={() => addAdjustment(preview.employee_id)}>
                              <Plus aria-hidden="true" /> {t('Add adjustment')}
                            </button>
                          </div>
                          {adjustmentFormOf(preview.employee_id).error && (
                            <p className="pc-adjust-error" role="alert">{adjustmentFormOf(preview.employee_id).error}</p>
                          )}
                          <p className="field-hint">{t('Adjustments are applied after tax. Something that should be taxed, like a bonus, is a benefit.')}</p>
                        </div>

                        {/* Net Pay Summary */}
                        <div className="pc-net">
                          <div className="pc-net__label">{t('NET PAY')}</div>
                          <div className="pc-net__amount">{preview.currency} {preview.net_pay.toLocaleString(locale, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                          <div className="pc-net__details">
                            {preview.gross_pay > 0 && (
                              <span>{t('{pct}% of gross pay', { pct: ((preview.net_pay / preview.gross_pay) * 100).toFixed(1) })}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="pc-form-actions is-sticky">
              <button onClick={() => setStep(1)} className="btn btn--secondary">
                {t('← Back to Selection')}
              </button>
              <button onClick={handleSaveAll} className="btn btn--primary" disabled={loading}>
                {loading ? t('Saving...') : <><Save aria-hidden="true" /> {t('Save {count} Payroll Record(s)', { count: previews.length })}</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PayrollCalculator;